import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api, jsonBody } from "../../api/client";
import type { ListResponse } from "../../api/types";
import { useSensitiveAction } from "../../auth/SensitiveActionGate";
import { formatDate } from "../../lib/format";
import {
  Card,
  CardHeader,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  StatusPill,
} from "../../ui/components";
import { PanelSkeleton } from "../../ui/skeletons";

interface OIDCConnection {
  id: string;
  name: string;
  providerKind: "entra" | "entra_usgov" | "okta" | "google" | "development";
  issuerUrl: string;
  clientId: string;
  domains: string[];
  enabled: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

const providerLabels: Record<OIDCConnection["providerKind"], string> = {
  entra: "Microsoft Entra ID",
  entra_usgov: "Microsoft Entra US Government",
  okta: "Okta / Okta Government",
  google: "Google Workspace",
  development: "Local development provider",
};

function providerReference(connection?: OIDCConnection) {
  if (!connection || connection.providerKind === "google") return "";
  try {
    const issuer = new URL(connection.issuerUrl);
    if (connection.providerKind === "entra" || connection.providerKind === "entra_usgov") {
      return issuer.pathname.split("/").filter(Boolean)[0] ?? "";
    }
    if (connection.providerKind === "okta") return issuer.hostname;
    return issuer.origin;
  } catch {
    return "";
  }
}

export function SSOPanel() {
  const sensitive = useSensitiveAction({
    setupTitle: "Protect identity-provider changes with two-step verification",
    lockedTitle: "Confirm your identity to manage single sign-on",
    lockedDescription:
      "Identity-provider changes affect how every staff account signs in. Complete a recent verification before saving or disabling a connection.",
  });
  const connections = useQuery({
    queryKey: ["sso", "connections"],
    queryFn: () => api<ListResponse<OIDCConnection>>("/api/v1/auth/sso/connections"),
    retry: false,
  });
  const [editing, setEditing] = useState<OIDCConnection>();
  const [disabling, setDisabling] = useState<OIDCConnection>();
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [saved, setSaved] = useState(false);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const providerKind = String(form.get("providerKind"));
    setWorking(true);
    setError(undefined);
    setSaved(false);
    try {
      await api<OIDCConnection>(
        editing ? `/api/v1/auth/sso/connections/${editing.id}` : "/api/v1/auth/sso/connections",
        {
          method: editing ? "PUT" : "POST",
          body: jsonBody({
            name: String(form.get("name")),
            providerKind,
            providerReference:
              providerKind === "google" ? "" : String(form.get("providerReference")),
            clientId: String(form.get("clientId")),
            clientSecret: String(form.get("clientSecret")),
            domains: String(form.get("domains"))
              .split(/[\s,]+/u)
              .map((value) => value.trim())
              .filter(Boolean),
            enabled: form.get("enabled") === "on",
            version: editing?.version ?? 0,
          }),
        },
      );
      setEditing(undefined);
      setSaved(true);
      await connections.refetch();
      event.currentTarget.reset();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function disable(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!disabling) return;
    setWorking(true);
    setError(undefined);
    try {
      await api<void>(`/api/v1/auth/sso/connections/${disabling.id}`, {
        method: "DELETE",
        body: jsonBody({ reason: String(new FormData(event.currentTarget).get("reason")) }),
      });
      setDisabling(undefined);
      await connections.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (connections.isLoading) return <PanelSkeleton table={false} />;
  return (
    <div className="stack">
      <Notice tone="info" title="Invited accounts only">
        SSO never provisions a new account. Invite and activate each staff member first, then their
        verified work-domain identity can be linked on first successful sign-in.
      </Notice>
      {sensitive.view}
      {error || connections.error ? <ErrorPanel error={error ?? connections.error} /> : null}
      {saved ? <Notice tone="success">Single sign-on connection saved.</Notice> : null}
      <div className="content-grid">
        <Card className="span-5">
          <CardHeader
            title="Configured providers"
            description="Domains are globally exclusive so a work address resolves to one tenant only."
          />
          {connections.data?.items.length ? (
            <div className="stack">
              {connections.data.items.map((item) => (
                <div className="embedded-panel stack" key={item.id}>
                  <div className="inline spread">
                    <strong>{item.name}</strong>
                    <StatusPill value={item.enabled ? "enabled" : "disabled"} />
                  </div>
                  <small>
                    {providerLabels[item.providerKind]} · {item.domains.join(", ")}
                  </small>
                  <small>
                    Issuer: <span className="mono">{item.issuerUrl}</span>
                  </small>
                  <small>Updated {formatDate(item.updatedAt)}</small>
                  <div className="inline">
                    <button
                      className="button secondary small"
                      type="button"
                      onClick={() => setEditing(item)}
                    >
                      Configure
                    </button>
                    {item.enabled ? (
                      <button
                        className="button danger small"
                        type="button"
                        disabled={!sensitive.unlocked}
                        onClick={() => setDisabling(item)}
                      >
                        Disable
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No identity provider"
              description="Add the organization’s approved provider before offering SSO to staff."
            />
          )}
        </Card>
        <Card className="span-7">
          <CardHeader
            title={editing ? `Configure ${editing.name}` : "Add an identity provider"}
            description="Use a dedicated OIDC web application and register the callback shown in the deployment guide."
          />
          <form className="form-grid" key={editing?.id ?? "new"} onSubmit={save}>
            <Field label="Connection name">
              <input name="name" required maxLength={100} defaultValue={editing?.name} />
            </Field>
            <Field label="Provider">
              <select name="providerKind" required defaultValue={editing?.providerKind ?? "entra"}>
                {Object.entries(providerLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Provider reference"
              hint="Entra directory ID, Okta organization hostname, or a loopback issuer for local development. Leave blank for Google."
            >
              <input
                name="providerReference"
                defaultValue={providerReference(editing)}
                placeholder="00000000-0000-0000-0000-000000000000"
              />
            </Field>
            <Field label="OIDC client ID">
              <input name="clientId" required defaultValue={editing?.clientId} autoComplete="off" />
            </Field>
            <Field
              label="OIDC client secret"
              hint={
                editing
                  ? "Leave blank to retain the stored secret."
                  : "Stored encrypted and never returned."
              }
            >
              <input
                name="clientSecret"
                type="password"
                required={!editing}
                autoComplete="new-password"
              />
            </Field>
            <Field
              label="Verified work domains"
              hint="Comma or space separated. Consumer email domains are not accepted."
            >
              <textarea
                name="domains"
                required
                defaultValue={editing?.domains.join(", ")}
                placeholder="agency.gov, example.com"
              />
            </Field>
            <label className="check">
              <input name="enabled" type="checkbox" defaultChecked={editing?.enabled ?? true} />{" "}
              Enable this connection
            </label>
            <div className="full inline">
              <FormActions
                submitting={working}
                disabled={!sensitive.unlocked}
                submitLabel={editing ? "Save connection" : "Add provider"}
              />
              {editing ? (
                <button
                  className="button ghost"
                  type="button"
                  onClick={() => setEditing(undefined)}
                >
                  Cancel edit
                </button>
              ) : null}
            </div>
          </form>
        </Card>
      </div>
      {disabling ? (
        <Card>
          <CardHeader
            title={`Disable ${disabling.name}`}
            description="Existing password sessions remain governed by normal session controls. The provider domains are released from SSO discovery."
          />
          <form className="form-stack" onSubmit={disable}>
            <Field label="Reason">
              <textarea
                name="reason"
                required
                minLength={10}
                defaultValue="Identity provider access is being withdrawn by the tenant owner."
              />
            </Field>
            <div className="inline">
              <button
                className="button danger"
                type="submit"
                disabled={working || !sensitive.unlocked}
              >
                Disable connection
              </button>
              <button
                className="button ghost"
                type="button"
                onClick={() => setDisabling(undefined)}
              >
                Cancel
              </button>
            </div>
          </form>
        </Card>
      ) : null}
    </div>
  );
}
